import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { FiEdit2, FiEye, FiPlus, FiSearch, FiTrash2, FiX } from 'react-icons/fi';
import Layout from '../components/Layout';
import Pagination from '../components/Pagination';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import { formatSingaporeDateTime, formatSingaporeDateTimeDmyAmPm } from '../utils/singaporeTime';

const emptyForm = {
    name: '',
    number: '',
    tgid: '',
    tgusername: '',
    remarks: '',
};

const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded-md text-blue-700 font-bold';

const formatReviewFor = (row) => {
    if (row?.SupermanMember) {
        const m = row.SupermanMember;
        return `Superman: ${m.name || '—'} (${m.webID || 'no webID'})`;
    }
    if (row?.godMember) {
        const m = row.godMember;
        return `God: ${[m.godID, m.godName, m.webID].filter(Boolean).join(' - ') || '—'}`;
    }
    return '—';
};

const formatDeliveryPlatforms = (platforms) => {
    if (!Array.isArray(platforms) || !platforms.length) return '—';
    if (typeof platforms[0] === 'string') {
        return platforms.map((name) => String(name || '').trim()).filter(Boolean).join('\n') || '—';
    }
    return platforms.map((row) => {
        const name = String(row?.platform || row?.name || '').trim() || '—';
        if (!row?.dateTime) return name;
        return `${name} (${formatSingaporeDateTimeDmyAmPm(row.dateTime)})`;
    }).join('\n');
};

const formatLastPostedDates = (dates) => {
    if (!Array.isArray(dates) || !dates.length) return '—';
    return dates
        .map((d) => formatSingaporeDateTimeDmyAmPm(d, ''))
        .filter(Boolean)
        .join('\n') || '—';
};

const SupermanArmyPage = () => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [showForm, setShowForm] = useState(false);
    const [formMode, setFormMode] = useState('create');
    const [formData, setFormData] = useState(emptyForm);
    const [selected, setSelected] = useState(null);
    const [saving, setSaving] = useState(false);
    const [showDelete, setShowDelete] = useState(false);
    const [showView, setShowView] = useState(false);
    const [viewReviews, setViewReviews] = useState([]);
    const [viewLoading, setViewLoading] = useState(false);
    const [viewPage, setViewPage] = useState(1);
    const [viewTotalPages, setViewTotalPages] = useState(1);
    const [viewTotal, setViewTotal] = useState(0);
    const [commandCopied, setCommandCopied] = useState(false);
    const copiedTimerRef = React.useRef(null);

    const authHeaders = () => ({
        Authorization: `Bearer ${localStorage.getItem('token')}`,
        'ngrok-skip-browser-warning': 'true',
    });

    const fetchItems = useCallback(async (page, search) => {
        try {
            setLoading(true);
            setError('');
            let url = `${process.env.REACT_APP_API_URL}/api/supermanarmies?page=${page}&limit=20&sort=name`;
            if (search) url += `&search=${encodeURIComponent(search)}`;
            const response = await axios.get(url, { headers: authHeaders() });
            if (response.data.success) {
                setItems(response.data.data || []);
                setTotal(response.data.pagination?.total || 0);
                setTotalPages(response.data.pagination?.pages || 1);
            }
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchArmyReviews = useCallback(async (armyId, page) => {
        try {
            setViewLoading(true);
            setError('');
            const url = `${process.env.REACT_APP_API_URL}/api/supermanarmies/${armyId}/reviews?page=${page}&limit=10`;
            const response = await axios.get(url, { headers: authHeaders() });
            if (response.data.success) {
                setViewReviews(response.data.data || []);
                setViewTotal(response.data.pagination?.total || 0);
                setViewTotalPages(response.data.pagination?.pages || 1);
            }
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        } finally {
            setViewLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchItems(currentPage, searchTerm);
    }, [currentPage, searchTerm, fetchItems]);

    useEffect(() => {
        if (showView && selected?._id) {
            fetchArmyReviews(selected._id, viewPage);
        }
    }, [showView, selected?._id, viewPage, fetchArmyReviews]);

    const openCreate = () => {
        setFormMode('create');
        setSelected(null);
        setFormData(emptyForm);
        setShowForm(true);
    };

    const openEdit = (item) => {
        setFormMode('edit');
        setSelected(item);
        setFormData({
            name: item.name || '',
            number: item.number || '',
            tgid: item.tgid || '',
            tgusername: item.tgusername || '',
            remarks: item.remarks || '',
        });
        setShowForm(true);
    };

    const openView = (item) => {
        setSelected(item);
        setViewPage(1);
        setViewReviews([]);
        setViewTotal(item.reviewCount || 0);
        setViewTotalPages(1);
        setShowView(true);
    };

    const copyInvitewhiteCommand = async () => {
        try {
            const url = `${process.env.REACT_APP_API_URL}/api/supermanarmies?page=1&limit=500&sort=name`;
            const response = await axios.get(url, { headers: authHeaders() });
            const armies = response.data?.success ? (response.data.data || []) : [];
            const tgids = armies
                .map((row) => String(row.tgid || '').trim())
                .filter(Boolean);
            const message = `/invitewhite add 8888${tgids.length ? ` ${tgids.join(' ')}` : ''}`;
            await navigator.clipboard.writeText(message);
            if (copiedTimerRef.current) clearTimeout(copiedTimerRef.current);
            setCommandCopied(true);
            copiedTimerRef.current = setTimeout(() => setCommandCopied(false), 2000);
        } catch (err) {
            setError(err.response?.data?.error || err.message || 'Failed to copy command');
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setSaving(true);
        setError('');
        try {
            const payload = {
                ...formData,
                tgusername: formData.tgusername.replace(/^@/, ''),
            };
            const response = formMode === 'edit'
                ? await axios.put(
                    `${process.env.REACT_APP_API_URL}/api/supermanarmies/${selected._id}`,
                    payload,
                    { headers: authHeaders() }
                )
                : await axios.post(
                    `${process.env.REACT_APP_API_URL}/api/supermanarmies`,
                    payload,
                    { headers: authHeaders() }
                );
            if (response.data.success) {
                setShowForm(false);
                setSelected(null);
                setFormData(emptyForm);
                fetchItems(currentPage, searchTerm);
            }
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!selected) return;
        setSaving(true);
        try {
            await axios.delete(
                `${process.env.REACT_APP_API_URL}/api/supermanarmies/${selected._id}`,
                { headers: authHeaders() }
            );
            setShowDelete(false);
            setSelected(null);
            fetchItems(currentPage, searchTerm);
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Layout>
            <div className="px-2 w-full max-w-none p-6">
                <div className="flex justify-between items-center mb-6">
                    <div>
                        <h1 className="text-2xl font-bold">Superman Army</h1>
                        <p className="text-gray-600">Total: {total}</p>
                    </div>
                    <button
                        type="button"
                        onClick={openCreate}
                        className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                        <FiPlus className="mr-2" /> Add Army
                    </button>
                </div>

                <div className="flex items-start justify-between gap-3 mb-4">
                    <div className="flex gap-2 flex-1">
                    <div className="relative flex-1 max-w-xl">
                        <FiSearch className="absolute left-3 top-3 text-gray-400" />
                        <input
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    setSearchTerm(searchInput);
                                    setCurrentPage(1);
                                }
                            }}
                            placeholder="Search name, number, TG ID or username"
                            className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md"
                        />
                    </div>
                    <button
                        type="button"
                        onClick={() => { setSearchTerm(searchInput); setCurrentPage(1); }}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md"
                    >
                        Search
                    </button>
                    </div>
                    <div className="relative shrink-0">
                        <button
                            type="button"
                            onClick={copyInvitewhiteCommand}
                            className="px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-900"
                        >
                            Copy to Invitewhite
                        </button>
                        {commandCopied && (
                            <div className="absolute right-0 mt-1 px-2 py-1 text-xs font-medium text-white bg-green-600 rounded shadow whitespace-nowrap z-10">
                                Command Copied
                            </div>
                        )}
                    </div>
                </div>

                {error && <div className="mb-4 p-4 bg-red-100 text-red-700 rounded-md">{error}</div>}

                <div className="bg-white rounded-lg shadow overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Number</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">TG ID</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">TG Username</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reviews</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last 3 posted</th>
                                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {loading ? (
                                <tr><td colSpan={7} className="px-4 py-6 text-center">Loading...</td></tr>
                            ) : items.length === 0 ? (
                                <tr><td colSpan={7} className="px-4 py-6 text-center text-gray-500">No army found</td></tr>
                            ) : items.map((item) => (
                                <tr key={item._id}>
                                    <td className="px-4 py-3 text-blue-700 font-medium">{item.name || '—'}</td>
                                    <td className="px-4 py-3">{item.number || '—'}</td>
                                    <td className="px-4 py-3">{item.tgid || '—'}</td>
                                    <td className="px-4 py-3">{item.tgusername ? `@${item.tgusername}` : '—'}</td>
                                    <td className="px-4 py-3">{item.reviewCount ?? 0}</td>
                                    <td className="px-4 py-3 text-sm whitespace-pre-line leading-5">
                                        {formatLastPostedDates(item.lastPostedDates)}
                                    </td>
                                    <td className="px-4 py-3 text-right space-x-1">
                                        <button
                                            type="button"
                                            onClick={() => openView(item)}
                                            className="p-1.5 text-blue-600 hover:bg-blue-100 rounded-full"
                                            title="View"
                                        >
                                            <FiEye />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => openEdit(item)}
                                            className="p-1.5 text-green-600 hover:bg-green-100 rounded-full"
                                        >
                                            <FiEdit2 />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => { setSelected(item); setShowDelete(true); }}
                                            className="p-1.5 text-red-600 hover:bg-red-100 rounded-full"
                                        >
                                            <FiTrash2 />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />

                {showView && selected && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                        <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl p-6 max-h-[90vh] flex flex-col">
                            <div className="flex justify-between items-center mb-4 shrink-0">
                                <h2 className="text-xl font-bold">View Superman Army</h2>
                                <button
                                    type="button"
                                    onClick={() => { setShowView(false); setSelected(null); setViewReviews([]); }}
                                >
                                    <FiX />
                                </button>
                            </div>
                            <div className="space-y-2 text-sm mb-4 shrink-0">
                                <p>
                                    <span className="text-gray-600">Name:</span>{' '}
                                    <span className="font-medium text-blue-700">{selected.name || '—'}</span>
                                </p>
                                <p>
                                    <span className="text-gray-600">Reviews:</span>{' '}
                                    <span className="font-medium">{viewTotal}</span>
                                </p>
                                <p>
                                    <span className="text-gray-600">Remarks:</span>{' '}
                                    <span className="font-medium whitespace-pre-wrap">{selected.remarks || '—'}</span>
                                </p>
                            </div>
                            <div className="border border-gray-200 rounded-md flex flex-col min-h-0 flex-1">
                                <div className="overflow-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                        <thead className="bg-gray-50 sticky top-0">
                                            <tr>
                                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Platforms</th>
                                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Comment</th>
                                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Remarks</th>
                                                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Review For</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200">
                                            {viewLoading ? (
                                                <tr><td colSpan={6} className="px-3 py-4 text-center text-gray-500">Loading...</td></tr>
                                            ) : viewReviews.length === 0 ? (
                                                <tr><td colSpan={6} className="px-3 py-4 text-center text-gray-500">No reviews posted</td></tr>
                                            ) : viewReviews.map((row) => (
                                                <tr key={row._id}>
                                                    <td className="px-3 py-2 text-sm whitespace-nowrap">
                                                        {formatSingaporeDateTime(row.reviewDateandTime || row.reviewDate)}
                                                    </td>
                                                    <td className="px-3 py-2 text-sm">{row.type || 'Massage Review'}</td>
                                                    <td className="px-3 py-2 whitespace-pre-line text-xs leading-5">
                                                        {formatDeliveryPlatforms(row.platforms)}
                                                    </td>
                                                    <td className="px-3 py-2 text-sm max-w-[200px] truncate" title={row.reviewComment || ''}>
                                                        {row.reviewComment || '—'}
                                                    </td>
                                                    <td className="px-3 py-2 text-sm max-w-[180px] whitespace-pre-wrap">
                                                        {row.remarks || '—'}
                                                    </td>
                                                    <td className="px-3 py-2 text-sm text-blue-700">{formatReviewFor(row)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                {viewTotal > 0 && (
                                    <div className="flex items-center justify-between gap-3 px-3 py-2 border-t border-gray-200 bg-gray-50 shrink-0">
                                        <span className="text-xs text-gray-600">
                                            Reviews {(viewPage - 1) * 10 + 1}–{Math.min(viewPage * 10, viewTotal)} of {viewTotal}
                                        </span>
                                        <div className="flex items-center gap-1">
                                            <button
                                                type="button"
                                                disabled={viewPage <= 1}
                                                onClick={() => setViewPage((p) => Math.max(1, p - 1))}
                                                className={`px-2 py-1 text-xs rounded border ${
                                                    viewPage <= 1
                                                        ? 'text-gray-400 border-gray-200 cursor-not-allowed'
                                                        : 'text-gray-700 border-gray-300 hover:bg-white'
                                                }`}
                                            >
                                                Prev
                                            </button>
                                            <span className="px-2 text-xs text-gray-600">
                                                {viewPage}/{viewTotalPages}
                                            </span>
                                            <button
                                                type="button"
                                                disabled={viewPage >= viewTotalPages}
                                                onClick={() => setViewPage((p) => Math.min(viewTotalPages, p + 1))}
                                                className={`px-2 py-1 text-xs rounded border ${
                                                    viewPage >= viewTotalPages
                                                        ? 'text-gray-400 border-gray-200 cursor-not-allowed'
                                                        : 'text-gray-700 border-gray-300 hover:bg-white'
                                                }`}
                                            >
                                                Next
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {showForm && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
                        <div className="bg-white rounded-lg shadow-xl w-full max-w-xl p-6">
                            <div className="flex justify-between items-center mb-4">
                                <h2 className="text-xl font-bold">
                                    {formMode === 'edit' ? 'Edit Superman Army' : 'Add Superman Army'}
                                </h2>
                                <button type="button" onClick={() => setShowForm(false)}><FiX /></button>
                            </div>
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div>
                                    <label className="block text-sm font-medium mb-1">Name *</label>
                                    <input
                                        required
                                        value={formData.name}
                                        onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                                        className={inputClass}
                                    />
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium mb-1">Number</label>
                                        <input
                                            value={formData.number}
                                            onChange={(e) => setFormData((p) => ({ ...p, number: e.target.value }))}
                                            className={inputClass}
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium mb-1">TG ID</label>
                                        <input
                                            value={formData.tgid}
                                            onChange={(e) => setFormData((p) => ({ ...p, tgid: e.target.value }))}
                                            className={inputClass}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">TG Username</label>
                                    <input
                                        value={formData.tgusername}
                                        onChange={(e) => setFormData((p) => ({ ...p, tgusername: e.target.value }))}
                                        placeholder="@username"
                                        className={inputClass}
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium mb-1">Remarks</label>
                                    <textarea
                                        rows={3}
                                        value={formData.remarks}
                                        onChange={(e) => setFormData((p) => ({ ...p, remarks: e.target.value }))}
                                        className={inputClass}
                                    />
                                </div>
                                <div className="flex justify-end gap-3">
                                    <button
                                        type="button"
                                        onClick={() => setShowForm(false)}
                                        className="px-4 py-2 border border-gray-300 rounded-md"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={saving}
                                        className="px-4 py-2 bg-blue-600 text-white rounded-md disabled:opacity-50"
                                    >
                                        {saving ? 'Saving...' : 'Save'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {showDelete && selected && (
                    <DeleteConfirmationModal
                        title="Delete Superman Army"
                        message="Delete this Superman Army sender?"
                        itemName={selected.name}
                        isLoading={saving}
                        onConfirm={handleDelete}
                        onCancel={() => { setShowDelete(false); setSelected(null); }}
                    />
                )}
            </div>
        </Layout>
    );
};

export default SupermanArmyPage;
